import React, {
  Children,
  Fragment,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';

import {
  StepContainer,
  StepDoneIcon,
  StepIndicatorContainer,
  StepLine,
  StepNumber,
  StepTitle,
  StepTop,
} from '#/components/StepIndicator/styled';

interface StepProps {
  children?: ReactNode;
  number?: number;
  isDone?: boolean;
  isCurrent?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}

export const Step = ({
  children,
  number,
  isDone,
  isCurrent,
  isFirst,
  isLast,
}: StepProps) => (
  <StepContainer data-selected={isCurrent}>
    <StepTop>
      <StepLine isDone={isCurrent || isDone} isVisible={!isFirst} />
      <StepNumber {...{ isCurrent, isDone }}>
        {isDone ? <StepDoneIcon /> : number}
      </StepNumber>
      <StepLine isDone={isDone} isVisible={!isLast} />
    </StepTop>
    <StepTitle>{children}</StepTitle>
  </StepContainer>
);

interface StepIndicatorProps {
  /**
   * Children should contain `Step` components. All props will be set automatically,
   * except the text inside the Step.
   * */
  children?: ReactNode;

  /** The current step, starting at 1. Should be between 1 and the number of steps. */
  currentStep: number;

  /** Additional CSS class names to support styled step indicators. */
  className?: string;
}

export const StepIndicator = ({
  children,
  currentStep,
  ...rest
}: StepIndicatorProps) => {
  // Pre-filter children to exclude falsy values (e.g. conditionally (not) rendered steps) and
  // non-React elements (e.g. strings or numbers). But unfortunately that's not good enough for
  // TypeScript, and we have to cast them to ReactElement to be able to clone them later.
  const validChildren = Children.toArray(children).filter(
    (child) => child && isValidElement(child)
  ) as ReactElement[];

  return (
    <StepIndicatorContainer {...rest}>
      {validChildren.map((child, index) => {
        const number = index + 1;
        const isDone = number < currentStep;
        const isCurrent = number === currentStep;
        const isFirst = number === 1;
        const isLast = number === validChildren.length;

        const stepWithProps = React.cloneElement(child, {
          number,
          isDone,
          isCurrent,
          isFirst,
          isLast,
        });

        return (
          <Fragment key={`step-${number}`}>
            {stepWithProps}
            {!isLast && <StepLine isDone={isDone} />}
          </Fragment>
        );
      })}
    </StepIndicatorContainer>
  );
};
